import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Calendar, TrendingUp, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { format, isToday, isTomorrow, startOfWeek, endOfWeek, isWithinInterval } from "date-fns";
import { AtRiskClientsWidget } from "./AtRiskClientsWidget";
import { UpcomingSessionsWidget } from "./UpcomingSessionsWidget";

interface ClinicianDashboardProps {
  clients: any[];
  sessions: any[];
  subscription: any;
  clientCount: number;
  maxClients: number | null;
  clinicianId: string;
  onViewClient?: (clientId: string) => void;
}

export function ClinicianDashboard({ 
  clients, 
  sessions, 
  subscription, 
  clientCount, 
  maxClients,
  clinicianId,
  onViewClient
}: ClinicianDashboardProps) {
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });

  const todaySessions = sessions.filter(s => {
    const sessionDate = new Date(s.session_date);
    return isToday(sessionDate) && s.status !== 'cancelled';
  });

  const tomorrowSessions = sessions.filter(s => {
    const sessionDate = new Date(s.session_date);
    return isTomorrow(sessionDate) && s.status !== 'cancelled';
  });

  const weekSessions = sessions.filter(s => {
    const sessionDate = new Date(s.session_date);
    return isWithinInterval(sessionDate, { start: weekStart, end: weekEnd }) && s.status !== 'cancelled';
  });

  const pendingPayments = sessions.filter(s => 
    s.status === 'completed' && s.payment_status === 'pending'
  );

  const completedThisWeek = weekSessions.filter(s => s.status === 'completed').length;
  const noShows = sessions.filter(s => s.status === 'no_show').length;

  const stats = [
    {
      title: "Active Clients",
      value: clientCount,
      subtitle: maxClients ? `of ${maxClients} max` : "unlimited",
      icon: Users,
      color: "text-primary"
    },
    {
      title: "Today's Sessions",
      value: todaySessions.length,
      subtitle: tomorrowSessions.length > 0 ? `${tomorrowSessions.length} tomorrow` : "No sessions tomorrow",
      icon: Calendar,
      color: "text-accent"
    },
    {
      title: "This Week",
      value: weekSessions.length,
      subtitle: `${completedThisWeek} completed`,
      icon: TrendingUp,
      color: "text-secondary"
    },
    {
      title: "Pending Payments",
      value: pendingPayments.length,
      subtitle: pendingPayments.length > 0 ? "Needs attention" : "All clear",
      icon: Clock,
      color: pendingPayments.length > 0 ? "text-destructive" : "text-primary"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className="bg-card/50 border-border/30">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color} opacity-80`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Today's Schedule */}
        <Card className="bg-card/50 border-border/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Today's Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todaySessions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No sessions scheduled for today</p>
            ) : (
              <div className="space-y-3">
                {todaySessions.map(session => (
                  <div 
                    key={session.id} 
                    className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border border-border/20"
                  >
                    <div className="text-center min-w-[60px]">
                      <p className="text-lg font-bold text-foreground">
                        {session.session_time?.slice(0, 5)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {session.duration_minutes}min
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        {session.client_name || 'Client'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {session.session_type} · {session.location_type.replace('_', ' ')}
                      </p>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${
                      session.status === 'confirmed' ? 'bg-primary/20 text-primary' :
                      session.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {session.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions & Alerts */}
        <Card className="bg-card/50 border-border/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-primary" />
              Alerts & Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingPayments.length > 0 && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <Clock className="h-5 w-5 text-destructive" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">Pending Payments</p>
                  <p className="text-sm text-muted-foreground">
                    {pendingPayments.length} sessions awaiting payment
                  </p>
                </div>
              </div>
            )}

            {noShows > 0 && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">No-Shows This Month</p>
                  <p className="text-sm text-muted-foreground">
                    {noShows} sessions marked as no-show
                  </p>
                </div>
              </div>
            )}

            {clientCount === maxClients && maxClients !== null && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Users className="h-5 w-5 text-amber-500" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">Client Limit Reached</p>
                  <p className="text-sm text-muted-foreground">
                    Upgrade your plan to add more clients
                  </p>
                </div>
              </div>
            )}

            {pendingPayments.length === 0 && noShows === 0 && clientCount < (maxClients || Infinity) && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">All Clear!</p>
                  <p className="text-sm text-muted-foreground">
                    No urgent actions needed
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Widgets Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        <AtRiskClientsWidget clinicianId={clinicianId} onViewClient={onViewClient} />
        <UpcomingSessionsWidget clinicianId={clinicianId} />
      </div>

      {/* Recent Clients */}
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Recent Client Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No clients yet. Invite your first client to get started!
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {clients.slice(0, 6).map(client => (
                <div 
                  key={client.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border border-border/20"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {client.client_profile?.display_name || 'Client'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {client.status} · {client.therappi_connected ? 'Therappi connected' : 'Manual entry'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
